import React, { useCallback, useLayoutEffect, useMemo, useRef } from 'react';
import { isTraceBoardItemEqual } from './utils/isTraceBoardItemEqual';
import { useTraceBoardContext } from './TraceBoardContext';
const TraceBoardConnector = props => {
    const { row, rowIndex, connector } = props;
    const pathRef = useRef(null);
    const { elements, selected } = useTraceBoardContext();
    const { elementsRef } = elements;
    const { sourceItem, targetItem } = useMemo(() => {
        const source = {
            id: connector.sourceId,
            columnId: connector.sourceColumnId
        };
        const target = {
            id: connector.targetId,
            columnId: connector.targetColumnId
        };
        return {
            sourceItem: row.items.find(item => isTraceBoardItemEqual(item, source)),
            targetItem: row.items.find(item => isTraceBoardItemEqual(item, target))
        };
    }, [connector, row]);
    // bold connectors of selected item
    const isSelected = useMemo(() => {
        return !!(selected &&
            ((sourceItem && isTraceBoardItemEqual(selected, sourceItem)) ||
                (targetItem && isTraceBoardItemEqual(selected, targetItem))));
    }, [selected, sourceItem, targetItem]);
    // use callback so that every call gets the updated values from elementsRef
    const getElements = useCallback(() => {
        const sourceItemElement = elementsRef.current.find(c => {
            return (sourceItem &&
                c.rowIndex === rowIndex &&
                isTraceBoardItemEqual(c.item, sourceItem));
        });
        const targetItemElement = elementsRef.current.find(c => {
            return (targetItem &&
                c.rowIndex === rowIndex &&
                isTraceBoardItemEqual(c.item, targetItem));
        });
        return { sourceItemElement, targetItemElement };
    }, [elementsRef, rowIndex, sourceItem, targetItem]);
    // calculate connector edges and svg path
    const calculatePath = useCallback(() => {
        var _a;
        const { sourceItemElement, targetItemElement } = getElements();
        if (!sourceItemElement || !targetItemElement) {
            return '';
        }
        const sourceElement = sourceItemElement.element;
        const targetElement = targetItemElement.element;
        const boardRowElement = (_a = sourceElement.parentElement) === null || _a === void 0 ? void 0 : _a.parentElement;
        if (!boardRowElement)
            return '';
        const boardRowRect = boardRowElement.getBoundingClientRect();
        const sourceRect = sourceElement.getBoundingClientRect();
        const targetRect = targetElement.getBoundingClientRect();
        const sourceX = sourceRect.right - boardRowRect.left;
        const sourceY = sourceRect.top - boardRowRect.top + sourceRect.height / 2;
        const targetX = targetRect.left - boardRowRect.left;
        const targetY = targetRect.top - boardRowRect.top + targetRect.height / 2;
        const mid = (targetX - sourceX) / 2;
        const midX = sourceX + mid;
        const paths = [
            `M${sourceX} ${sourceY}`,
            `C${midX + mid} ${sourceY}`,
            `${midX - mid} ${targetY}`,
            `${targetX} ${targetY}`,
            `L${targetX} ${targetY}`
        ];
        return paths.join(' ');
    }, [getElements]);
    useLayoutEffect(() => {
        const onItemElementChange = () => {
            if (pathRef.current) {
                const path = calculatePath();
                pathRef.current.setAttribute('d', path);
            }
        };
        const columnId = connector.sourceColumnId;
        columnId && elements.subscribe(columnId, rowIndex, onItemElementChange);
        return () => {
            columnId && elements.unsubscribe(columnId, rowIndex, onItemElementChange);
        };
    }, [connector.sourceColumnId, elements, rowIndex, calculatePath]);
    return (React.createElement("g", null,
        React.createElement("path", { fill: 'none', ref: pathRef, stroke: '#007aff', strokeWidth: isSelected ? 3 : 2, strokeDasharray: isSelected ? 0 : '4,4' })));
};
export default TraceBoardConnector;
//# sourceMappingURL=TraceBoardConnector.js.map